import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { artistItemsAPI } from '../../api/artistItemsAPI';
import { analyticsAPI } from '../../api/itemEngagementAPI';
import { trackItemClicked, trackPaintingCustomization } from '../../utils/analytics';
import { formatItemPriceEgp } from '../../utils/formatPrice';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import FeaturedStrip from '../../components/items/FeaturedStrip';
import { DESIGNER_BROWSE_CATEGORIES } from '../../constants/designerBrowseCategories';
import BrowseFiltersToolbar, { buildBrowseFetchParams } from '../../components/items/BrowseFiltersToolbar';
import { EMPTY_BROWSE_FILTERS, SHOP_DEPARTMENTS } from '../../constants/itemBrowseFilters';
import VerticalPageSeo from '../../components/seo/VerticalPageSeo';
import { getSectionBrand } from '../../constants/sectionBrands';
import { getArtistItemPublicPath, getListingLinkState } from '../../utils/itemLinks';
import ItemFavoriteButton from '../../components/items/ItemFavoriteButton';
import { resolveListingBrand } from '../../components/items/ListingDetailShared';
import { FAVORITE_KIND } from '../../utils/favoriteKeys';
import { getApproximateViewerCoordinates } from '../../utils/viewerGeo';
import { canOwnerManageShopInPortal } from '../../utils/shopOwnerPortal';
import { customizationsAPI } from '../../api/customizationsAPI';

const ArtistsPageShell = styled.div`
  min-height: calc(100vh - 120px);
  background: #f6f1e8;
`;

const ArtistsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0.5rem 1rem 3rem;
`;

const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 1rem;
  }
`;

const ItemCard = styled(Link)`
  display: block;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  background: transparent;
  overflow: hidden;
  border: none;
  transition: opacity 0.15s ease;
  &:hover {
    opacity: 0.92;
  }
`;

const ItemImage = styled.div`
  width: 100%;
  aspect-ratio: 1;
  background: var(--bg-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-light);
  font-size: 3rem;
  position: relative;
  overflow: hidden;
  > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
    transform: scale(1.1);
  }
`;

const ItemContent = styled.div`
  padding: 0.55rem 0.15rem 0.25rem;
`;

const ItemPrice = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.2rem;
`;

const ItemMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.75rem;
  color: var(--text-light);
  min-width: 0;
`;

const MetaBrand = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const NoItemsMessage = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-secondary);
  h3 {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
    color: var(--text-primary);
  }
`;

const LoadMoreSentinel = styled.div`
  width: 100%;
  height: 1px;
`;

const IMAGE_BASE = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ? 'http://127.0.0.1:8080'
  : '';

const HeroActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 2rem;
`;

const CreateShopButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem 1.75rem;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  text-decoration: none;
  border-radius: 0.5rem;
  font-weight: 700;
  font-size: 1rem;
  box-shadow: 0 4px 14px rgba(5, 150, 105, 0.35);
  transition: all 0.2s;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(5, 150, 105, 0.45);
    color: white;
  }
`;

const ManageShopButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem 1.75rem;
  background: white;
  color: #059669;
  border: 2px solid #059669;
  text-decoration: none;
  border-radius: 0.5rem;
  font-weight: 700;
  font-size: 1rem;
  transition: all 0.2s;
  &:hover { background: #ecfdf5; }
`;

const CustomPaintingCard = styled.section`
  background:
    radial-gradient(circle at 88% 18%, rgba(255, 255, 255, 0.28), transparent 23%),
    linear-gradient(135deg, #173e31 0%, #2f674f 58%, #a87543 140%);
  border-radius: 1.1rem;
  box-shadow: 0 16px 38px rgba(23, 62, 49, 0.2);
  color: #fff;
  margin-bottom: 2rem;
  overflow: hidden;
  padding: clamp(1.35rem, 4vw, 2.4rem);
  h2 { font-size: clamp(1.7rem, 4vw, 2.45rem); margin: 0.35rem 0 0.65rem; }
  p { line-height: 1.6; margin: 0; max-width: 650px; }
`;
const CommissionEyebrow = styled.div`
  font-size: 0.76rem; font-weight: 900; letter-spacing: 0.16em;
`;
const CommissionActions = styled.div`
  display: flex; flex-wrap: wrap; gap: 0.75rem; margin-top: 1.25rem;
`;
const CommissionLink = styled(Link)`
  background: ${(props) => props.$secondary ? 'transparent' : '#fff'};
  border: 1.5px solid #fff; border-radius: 0.65rem;
  color: ${(props) => props.$secondary ? '#fff' : '#173e31'};
  font-weight: 850; padding: 0.75rem 1rem; text-decoration: none;
`;

const ArtistsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const brand = getSectionBrand('artists', 'artists');
  const { isAuthenticated } = useSelector(s => s.auth);
  const [items, setItems] = useState([]);
  const [myShop, setMyShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [browseFilters, setBrowseFilters] = useState({ ...EMPTY_BROWSE_FILTERS });
  const [sort, setSort] = useState('');
  const [viewerCoords, setViewerCoords] = useState(null);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [customizationEntry, setCustomizationEntry] = useState({ loading: false, draft: null, hasRequests: false });
  const loadMoreRef = useRef(null);
  const loadMoreObserverRef = useRef(null);

  const artistImpSent = useRef(new Set());
  const artistObserverRef = useRef(null);

  useEffect(() => {
    const department = searchParams.get('department') || '';
    if (department && !SHOP_DEPARTMENTS.includes(department)) {
      return;
    }
    setBrowseFilters((prev) => (
      prev.department === department ? prev : { ...prev, department }
    ));
  }, [searchParams]);

  const artistCardRef = useCallback((node) => {
    if (!node) return;
    if (!artistObserverRef.current) {
      artistObserverRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const id = Number(entry.target.dataset.artistItemId);
              if (id && !artistImpSent.current.has(id)) {
                artistImpSent.current.add(id);
                analyticsAPI.recordImpression(id, 'artist_item');
              }
            }
          });
        },
        { threshold: 0.5 },
      );
    }
    artistObserverRef.current.observe(node);
  }, []);

  useEffect(() => {
    return () => {
      analyticsAPI.flushImpressions();
      artistObserverRef.current?.disconnect();
    };
  }, []);

  useEffect(() => {
    getApproximateViewerCoordinates().then((c) => {
      if (c) setViewerCoords(c);
    });
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      artistItemsAPI.getMyShop().then(shop => setMyShop(shop)).catch(() => {});
      setCustomizationEntry((value) => ({ ...value, loading: true }));
      Promise.all([customizationsAPI.getDraft(), customizationsAPI.listBuyerRequests()])
        .then(([draft, requests]) => {
          setCustomizationEntry({
            loading: false,
            draft,
            hasRequests: Boolean(requests.results.length),
          });
        })
        .catch(() => setCustomizationEntry({ loading: false, draft: null, hasRequests: false }));
    } else {
      setCustomizationEntry({ loading: false, draft: null, hasRequests: false });
    }
  }, [isAuthenticated]);

  const fetchPage = useCallback(async (targetPage, replace = false) => {
    const geoExtra = viewerCoords
      ? { user_lat: viewerCoords.lat, user_lon: viewerCoords.lon }
      : {};
    const filters = buildBrowseFetchParams(browseFilters, sort, geoExtra);
    if (replace) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    try {
      const payload = await artistItemsAPI.getArtistItemsPage(filters, targetPage, 24);
      const nextResults = Array.isArray(payload?.results) ? payload.results : [];
      setItems(prev => (replace ? nextResults : [...prev, ...nextResults]));
      setPage(payload?.page || targetPage);
      setHasNext(Boolean(payload?.has_next));
      setTotalCount(payload?.count || nextResults.length);
    } catch (err) {
      console.error('ArtistsPage: Failed to fetch items', err);
      if (replace) {
        setItems([]);
        setHasNext(false);
        setTotalCount(0);
      }
    } finally {
      if (replace) setLoading(false);
      else setLoadingMore(false);
    }
  }, [browseFilters, sort, viewerCoords]);

  useEffect(() => {
    fetchPage(1, true);
  }, [fetchPage]);

  useEffect(() => {
    if (!loadMoreRef.current) return;
    if (loadMoreObserverRef.current) loadMoreObserverRef.current.disconnect();
    loadMoreObserverRef.current = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first?.isIntersecting && hasNext && !loadingMore && !loading) {
          fetchPage(page + 1, false);
        }
      },
      { rootMargin: '300px 0px 300px 0px', threshold: 0.01 }
    );
    loadMoreObserverRef.current.observe(loadMoreRef.current);
    return () => loadMoreObserverRef.current?.disconnect();
  }, [hasNext, loadingMore, loading, page, fetchPage]);

  const handleFiltersChange = (next) => {
    setBrowseFilters(next);
  };

  const getImageUrl = (item) => {
    if (item.primary_image) {
      return item.primary_image.startsWith('http') ? item.primary_image : `${IMAGE_BASE}${item.primary_image}`;
    }
    if (item.image_url) return item.image_url;
    if (item.images && item.images.length > 0) {
      const img = item.images[0].image;
      return img.startsWith('http') ? img : `${IMAGE_BASE}${img}`;
    }
    return null;
  };

  if (loading && items.length === 0) {
    return <ArtistsPageShell><LoadingSpinner text="Loading items..." /></ArtistsPageShell>;
  }

  const pageContent = (
    <ArtistsContainer>
      <VerticalPageSeo />

      <HeroActions>
        {canOwnerManageShopInPortal(myShop) ? (
          <ManageShopButton to={`/artist-shop/${artistItemsAPI.buildShopRef(myShop)}`}>
            {brand.myShop}
          </ManageShopButton>
        ) : (
          <CreateShopButton to={{ pathname: '/create-shop', search: '?kind=artists' }}>
            {brand.createShop}
          </CreateShopButton>
        )}
      </HeroActions>

      <CustomPaintingCard aria-busy={customizationEntry.loading}>
        <CommissionEyebrow>MADE FOR YOU</CommissionEyebrow>
        <h2>Have a painting made for you</h2>
        <p>Describe what you have in mind, discover matching artists, and ask one for a quote.</p>
        <CommissionActions>
          {customizationEntry.loading ? (
            <span>Checking your saved request…</span>
          ) : (
            <CommissionLink
              to={customizationEntry.draft
                ? `/custom-painting/${customizationEntry.draft.id}/step/${JSON.parse(localStorage.getItem(`flea:custom-painting:${customizationEntry.draft.id}`) || '{}').lastStep || 1}`
                : '/custom-painting'}
              onClick={() => trackPaintingCustomization('painting_customize_clicked', {
                entry_point: 'artists_browse',
              })}
            >
              {customizationEntry.draft ? 'Continue your custom painting' : 'Create a custom painting'}
            </CommissionLink>
          )}
          {(customizationEntry.hasRequests || customizationEntry.draft) && (
            <CommissionLink to="/customization-requests" $secondary>My customization requests</CommissionLink>
          )}
        </CommissionActions>
      </CustomPaintingCard>

      <FeaturedStrip
        scope={brand.featuredScope}
        department={browseFilters.department || ''}
      />

      <BrowseFiltersToolbar
        filters={browseFilters}
        onFiltersChange={handleFiltersChange}
        categories={DESIGNER_BROWSE_CATEGORIES}
        sort={sort}
        onSortChange={setSort}
        hideConditionFilter
      />

      {items.length === 0 ? (
        <NoItemsMessage>
          <h3>No items yet.</h3>
          <p>Try selecting a different category or be the first to list an item!</p>
        </NoItemsMessage>
      ) : (
        <>
          <ItemsGrid>
            {items.map((item) => {
              const imageUrl = getImageUrl(item);
              const hasMultipleImages = item.images && item.images.length > 1;
              const itemPath = getArtistItemPublicPath(item);
              const linkState = getListingLinkState(item, 'artist_item');
              return (
                <ItemCard
                  key={item.key || `artist_item-${item.id}`}
                  to={itemPath}
                  state={linkState}
                  onClick={(e) => {
                    trackItemClicked(item.id, { item_type: 'artist_item', category: item.category });
                    analyticsAPI.recordClick(item.id, 'artist_item');
                    e.preventDefault();
                    navigate(itemPath, { state: linkState });
                  }}
                  ref={artistCardRef}
                  data-artist-item-id={item.id}
                >
                  <ItemImage style={{ position: 'relative' }}>
                    <ItemFavoriteButton
                      itemId={item.id}
                      favoriteKind={FAVORITE_KIND.ITEM}
                      category={item.category}
                    />
                    {imageUrl ? (
                      <>
                        <img
                          src={imageUrl}
                          alt={item.title}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', transform: 'scale(1.1)' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        {hasMultipleImages && (
                          <div style={{
                            position: 'absolute', top: '8px', left: '8px',
                            backgroundColor: 'rgba(0,0,0,0.6)', color: 'white',
                            padding: '4px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600'
                          }}>
                            📷 {item.images.length}
                          </div>
                        )}
                      </>
                    ) : (
                      <div style={{ display: 'flex', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center', fontSize: '3rem', color: 'var(--text-light)' }}>
                        🎨
                      </div>
                    )}
                  </ItemImage>
                  <ItemContent>
                    <ItemPrice>{formatItemPriceEgp(item.price)}</ItemPrice>
                    <ItemMeta>
                      <MetaBrand>{resolveListingBrand(item) || '—'}</MetaBrand>
                    </ItemMeta>
                  </ItemContent>
                </ItemCard>
              );
            })}
          </ItemsGrid>
          {loadingMore && <LoadingSpinner text="Loading more art..." />}
          {!hasNext && items.length > 0 && (
            <NoItemsMessage style={{ paddingTop: '1.2rem', paddingBottom: '1.2rem' }}>
              <p>Showing {items.length} of {totalCount} items.</p>
            </NoItemsMessage>
          )}
          <LoadMoreSentinel ref={loadMoreRef} />
        </>
      )}
    </ArtistsContainer>
  );

  return <ArtistsPageShell>{pageContent}</ArtistsPageShell>;
};

export default ArtistsPage;
